import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, LogOut, RefreshCw, Loader2, Lock } from 'lucide-react';
import { formatINR } from '../lib/plans';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'pallavit_admin_passcode';

const statusBadge = (s) => {
  const map = {
    paid: 'bg-brand-forest text-brand-bg',
    created: 'bg-brand-gold/30 text-brand-ink',
    signature_failed: 'bg-red-100 text-red-700',
  };
  return map[s] || 'bg-brand-line text-brand-ink';
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

function toCSV(orders) {
  const headers = [
    'order_id', 'status', 'created_at', 'paid_at',
    'plan_key', 'tree_key', 'amount_paise', 'currency',
    'sender_name', 'recipient_name', 'occasion', 'message',
    'addon_keys', 'razorpay_order_id', 'razorpay_payment_id',
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = Array.isArray(v) ? v.join('|') : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = orders.map((o) => headers.map((h) => esc(o[h])).join(','));
  return [headers.join(','), ...rows].join('\n');
}

export default function AdminPage() {
  const [passcode, setPasscode] = React.useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [authed, setAuthed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState({ stats: null, orders: [] });

  const fetchOrders = React.useCallback(async (code) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/orders`, {
        headers: { 'X-Admin-Passcode': code },
      });
      if (res.status === 401) {
        setError('Invalid passcode');
        setAuthed(false);
        sessionStorage.removeItem(STORAGE_KEY);
        return false;
      }
      if (!res.ok) {
        setError('Could not load orders.');
        return false;
      }
      const j = await res.json();
      setData(j);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, code);
      return true;
    } catch (e) {
      setError('Network error.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load if passcode already cached in session
  React.useEffect(() => {
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) fetchOrders(cached);
  }, [fetchOrders]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    await fetchOrders(passcode.trim());
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPasscode('');
    setData({ stats: null, orders: [] });
  };

  const downloadCSV = () => {
    const csv = toCSV(data.orders);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pallavit-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={handleLogin} className="w-full max-w-md bg-brand-bg2 border border-brand-line rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-brand-forest text-brand-bg flex items-center justify-center">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <div className="font-serif text-2xl text-brand-ink leading-tight">Pallavit admin</div>
              <div className="text-brand-ink2 text-xs tracking-wider uppercase">Order dashboard</div>
            </div>
          </div>
          <label className="block text-brand-ink2 text-[11px] tracking-widest uppercase mb-2">Passcode</label>
          <input
            type="password"
            autoFocus
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Enter admin passcode"
            data-testid="admin-passcode-input"
            className="w-full rounded-xl border border-brand-line bg-brand-bg px-4 py-3 text-brand-ink focus:outline-none focus:border-brand-forest"
          />
          {error && <div className="mt-3 text-red-600 text-sm" data-testid="admin-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-6 w-full rounded-full bg-brand-forest text-brand-bg py-3 text-sm tracking-wide font-medium hover:bg-brand-ink transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : 'Unlock dashboard →'}
          </button>
          <Link to="/" className="mt-5 inline-flex items-center gap-2 text-brand-ink2 text-xs hover:text-brand-forest">
            <ArrowLeft className="w-3 h-3" /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  const { stats, orders } = data;

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      <div className="sticky top-0 z-20 bg-brand-bg border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-brand-ink2 hover:text-brand-forest" data-testid="admin-back-link">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <div className="font-serif text-xl text-brand-ink leading-none">Pallavit admin</div>
              <div className="text-brand-ink2 text-[10px] tracking-[0.25em] uppercase mt-1">Orders</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchOrders(sessionStorage.getItem(STORAGE_KEY))}
              data-testid="admin-refresh-button"
              className="rounded-full border border-brand-line bg-brand-bg2 hover:border-brand-gold/60 px-4 py-2 text-sm text-brand-ink inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={downloadCSV}
              disabled={!orders.length}
              data-testid="admin-csv-button"
              className="rounded-full bg-brand-forest text-brand-bg px-4 py-2 text-sm hover:bg-brand-ink transition-all inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="rounded-full border border-brand-line hover:bg-brand-bg2 px-4 py-2 text-sm text-brand-ink2 inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="admin-stats">
            <div className="rounded-2xl bg-brand-bg2 border border-brand-line p-6">
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-ink2 mb-2">Total orders</div>
              <div className="font-serif text-4xl text-brand-ink">{stats.total_orders}</div>
            </div>
            <div className="rounded-2xl bg-brand-bg2 border border-brand-line p-6">
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-ink2 mb-2">Paid orders</div>
              <div className="font-serif text-4xl text-brand-forest">{stats.paid_orders}</div>
            </div>
            <div className="rounded-2xl bg-brand-forest text-brand-bg p-6">
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-gold mb-2">Revenue (paid)</div>
              <div className="font-serif text-4xl">{formatINR(stats.revenue_inr)}</div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-brand-line overflow-hidden" data-testid="admin-orders-table">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-brand-bg2 text-brand-ink2 text-[11px] tracking-widest uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-left px-4 py-3">Recipient</th>
                  <th className="text-left px-4 py-3">From</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-left px-4 py-3">Tree</th>
                  <th className="text-left px-4 py-3">Occasion</th>
                  <th className="text-left px-4 py-3">Add-ons</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && !loading && (
                  <tr><td colSpan={9} className="text-center py-14 text-brand-ink2 italic font-serif">No orders yet. When your first gift arrives, it will appear here.</td></tr>
                )}
                {orders.map((o, i) => (
                  <tr key={o.order_id || i} className="border-t border-brand-line hover:bg-brand-bg2/60" data-testid={`admin-order-row-${i}`}>
                    <td className="px-4 py-3 text-brand-ink2 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-3 text-brand-ink font-medium">{o.recipient_name}</td>
                    <td className="px-4 py-3 text-brand-ink2">{o.sender_name}</td>
                    <td className="px-4 py-3 text-brand-ink capitalize">{o.plan_key}</td>
                    <td className="px-4 py-3 text-brand-ink capitalize">{o.tree_key}</td>
                    <td className="px-4 py-3 text-brand-ink2">{o.occasion}</td>
                    <td className="px-4 py-3 text-brand-ink2 text-xs">
                      {(o.addon_keys || []).length > 0 ? (o.addon_keys || []).join(', ') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-serif text-brand-ink">
                      {formatINR(Math.round((o.amount_paise || 0) / 100))}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase ${statusBadge(o.status)}`}>
                        {o.status || 'unknown'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
